import { Injectable, computed, inject, Injector } from '@angular/core';
import { DataService } from './data.service';
import { AuthService } from '../auth/auth.service';
import { ActingContextService } from './acting-context.service';
import { Ticket, TicketStatus, TicketSubject, TicketMessage } from '../models/loan.model';
import { addDoc, collection, doc, updateDoc, arrayUnion, writeBatch, getDoc } from 'firebase/firestore'; // FIX: Consolidated imports
import { FIRESTORE } from '../firebase.config';
import { TICKET_STATUS } from '../constants';

@Injectable({
  providedIn: 'root'
})
export class TicketService {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private injector = inject(Injector);

  // Computed Signals
  allTicketsSorted = computed(() => {
    const statusOrder: Record<TicketStatus, number> = { [TICKET_STATUS.OPEN]: 1, [TICKET_STATUS.ANSWERED]: 2, [TICKET_STATUS.RESOLVED]: 3 };
    return [...this.dataService.tickets()].sort((a, b) => {
      if (a.status !== b.status) return statusOrder[a.status] - statusOrder[b.status];
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  });

  ticketsForCurrentUser = computed(() => {
    // Use Injector to get ActingContextService lazily
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;

    if (!effectiveUserId) return [];
    return this.allTicketsSorted().filter(t => t.userId === effectiveUserId);
  });

  openTicketsCount = computed(() => this.allTicketsSorted().filter(t => t.status === TICKET_STATUS.OPEN).length);

  recentTickets = computed(() => this.allTicketsSorted().slice(0, 5));

  // Write Methods
  async addTicket(data: { subject: TicketSubject; message: string; userId: string; userName: string; }): Promise<void> {
    console.log('[TICKET DEBUG] addTicket called with data:', data);
    console.log('[TICKET DEBUG] subject type:', typeof data.subject, 'value:', data.subject);

    // Validate input data
    if (data.subject === undefined || data.subject === null) {
      console.error('[TICKET DEBUG] Missing subject');
      throw new Error('Ticket subject is required');
    }
    if (!data.message || data.message.trim() === '') {
      console.error('[TICKET DEBUG] Missing or empty message');
      throw new Error('Ticket message is required');
    }
    if (!data.userId) {
      console.error('[TICKET DEBUG] Missing userId');
      throw new Error('User ID is required');
    }
    if (!data.userName) {
      console.error('[TICKET DEBUG] Missing userName');
      throw new Error('User name is required');
    }

    const now = new Date().toISOString();

    const newTicketRef = doc(collection(FIRESTORE, 'tickets'));
    const ticketId = newTicketRef.id;

    const newTicket: Ticket = {
      id: ticketId,
      userId: data.userId,
      userName: data.userName,
      subject: data.subject,
      subjectDisplay: data.subject.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      status: TICKET_STATUS.OPEN,
      createdAt: now,
      updatedAt: now,
      messages: [{
        id: `msg-${Date.now()}`,
        ticketId: ticketId,
        senderId: data.userId,
        senderName: data.userName,
        message: data.message,
        timestamp: now,
      }]
    };

    console.log('[TICKET DEBUG] Created ticket object:', newTicket);
    console.log('[TICKET DEBUG] Writing to paths:', {
      topLevel: `tickets/${ticketId}`,
      userSubcollection: `users/${data.userId}/tickets/${ticketId}`
    });

    const batch = writeBatch(FIRESTORE);
    const userTicketRef = doc(FIRESTORE, `users/${data.userId}/tickets/${ticketId}`);

    batch.set(newTicketRef, newTicket);
    batch.set(userTicketRef, newTicket);

    try {
      await batch.commit();
      console.log('[TICKET DEBUG] Ticket created successfully');
    } catch (error) {
      console.error('[TICKET DEBUG] Error creating ticket:', error);
      throw error;
    }
  }

  async addMessageToTicket(ticketId: string, message: string, senderId: string, senderName: string): Promise<void> {
    const topLevelRef = doc(FIRESTORE, 'tickets', ticketId);

    const ticketDoc = await getDoc(topLevelRef);
    if (!ticketDoc.exists()) {
      throw new Error(`Ticket with id ${ticketId} not found.`);
    }
    const userId = ticketDoc.data().userId;

    const batch = writeBatch(FIRESTORE);
    const nestedRef = doc(FIRESTORE, `users/${userId}/tickets/${ticketId}`);

    const newMessage: TicketMessage = {
      id: `msg-${Date.now()}`,
      ticketId,
      senderId,
      senderName,
      message,
      timestamp: new Date().toISOString(),
    };
    const newStatus: TicketStatus = senderId.includes('admin') ? TICKET_STATUS.ANSWERED : TICKET_STATUS.OPEN;

    const updatePayload = {
      messages: arrayUnion(newMessage),
      status: newStatus,
      updatedAt: new Date().toISOString()
    };

    batch.update(topLevelRef, updatePayload);
    batch.update(nestedRef, updatePayload);

    await batch.commit();
  }

  async updateTicketStatus(ticketId: string, newStatus: TicketStatus): Promise<void> {
    const topLevelRef = doc(FIRESTORE, 'tickets', ticketId);

    const ticketDoc = await getDoc(topLevelRef);
    if (!ticketDoc.exists()) {
      throw new Error(`Ticket with id ${ticketId} not found.`);
    }
    const userId = ticketDoc.data().userId;

    const batch = writeBatch(FIRESTORE);
    const nestedRef = doc(FIRESTORE, `users/${userId}/tickets/${ticketId}`);

    const updatePayload = {
      status: newStatus,
      updatedAt: new Date().toISOString()
    };

    batch.update(topLevelRef, updatePayload);
    batch.update(nestedRef, updatePayload);

    await batch.commit();
  }
}